/* 2009-2018 Copyright Ridecell Inc. All rights reserved. */
import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { IntlProvider, addLocaleData } from 'react-intl'
import axios from 'axios'
import en from 'react-intl/locale-data/en'
import es from 'react-intl/locale-data/es'
import it from 'react-intl/locale-data/it'
import pl from 'react-intl/locale-data/pl'
import navigator from './util/navigator'
import window from './util/window'
import config from "../config/config";

addLocaleData([...en, ...es, ...it, ...pl])
const baseURL = 'https://api.phraseapp.com/api/v2/projects'
export class BundleProvider extends Component {
  static propTypes = {
    children: PropTypes.node,
    defaultLocale: PropTypes.string,
    forceLocale: PropTypes.string,
  }

  constructor(props, context = {}) {
    super(props, context)

    this.state = {
      accessToken: config.accessToken,
      currLocale: null,
      isLoading: true,
      lastModified: null,
      projectID: '',
      supportedLocales: null,
    }
  }


  async componentDidMount() {
    this.initSessionStorage()
    const { defaultLocale, forceLocale } = this.props
    const supportedLocales = await this.getSupportedLocales()

    let currLocale = ''
    if (forceLocale) {
      currLocale = forceLocale.slice(0, 2)
    } else if (this.pickBrowserLocale(supportedLocales) !== defaultLocale.slice(0, 2)) {
      currLocale = this.pickBrowserLocale(supportedLocales)
    } else {
      currLocale = defaultLocale.slice(0, 2)
    }
    // This is valid according to React docs, rule is outdated
    // https://github.com/yannickcr/eslint-plugin-react/issues/1754
    // eslint-disable-next-line
    await this.fetchBundle(currLocale, supportedLocales)
  }

  getSupportedLocales = async () => {
    const { projectID } = this.props
    const { accessToken } = this.state
    let supportedLocales

    await axios
      .get(`${baseURL}/${projectID}/locales?file_format=react_simple_json`, {
        headers: { Authorization: `token ${accessToken}` },
      })
      .then(res => {
        supportedLocales = res.data
        return supportedLocales
      })
      .catch(error => {
        console.error('Locale Fetch Failed', error) // eslint-disable-line no-console
        return null
      })
    return supportedLocales
  }

  getSessionStorage = () => {
    if (window.sessionStorage.getItem('bundles')) {
      return JSON.parse(window.sessionStorage.getItem('bundles'))
    }
    return {}
  }

  setSessionStorage = bundles => {
    if (window.sessionStorage.getItem('bundles')) {
      window.sessionStorage.setItem('bundles', JSON.stringify(bundles))
    }
  }

  initSessionStorage = () => {
    if (!window.sessionStorage.getItem('bundles')) {
      window.sessionStorage.setItem('bundles', JSON.stringify({}))
    }
  }

  addSessionBundle = () => {
    const { bundle, currLocale, lastModified } = this.state
    const sessionStorage = this.getSessionStorage()
    const newSession = { ...sessionStorage, [currLocale]: { bundle, lastModified } }
    this.setSessionStorage(newSession)
  }

  pickBrowserLocale = supportedLocales => {
    if (supportedLocales) {
      const key = navigator.languages.find(language => {
        const languageAbbrev = language.slice(0, 2)
        return supportedLocales.find(
          locale => locale.name.toLowerCase().slice(0,2) === languageAbbrev.toLowerCase()
        )
      })
      if (key) {
        return key.slice(0, 2)
      }
    }
    return null
  }

  fetchBundle = async (currLocale, supportedLocales) => {
    const { projectID } = this.props
    const { accessToken } = this.state
    const sessionStorage = this.getSessionStorage()
    const newLocale = supportedLocales.find(sl => sl.name.slice(0,2) === currLocale)
    const localeLastModified = sessionStorage[currLocale]
      ? sessionStorage[currLocale].lastModified
      : null
    const prevLocaleBundle = sessionStorage[currLocale] ? sessionStorage[currLocale].bundle : null
    await axios
      .get(
        `${baseURL}/${projectID}/locales/${newLocale.id}/download?file_format=react_simple_json`,
        {
          headers: {
            Authorization: `token ${accessToken}`,
            'If-Modified-Since': localeLastModified,
          },
          validateStatus: status => status < 400,
        }
      )
      .then(res => {
        const lastModified = res.headers['last-modified']
        const resDataLength = Object.keys(res.data).length
        if (resDataLength !== 0) {
          this.setState({ bundle: res.data, isLoading: false, lastModified }, () =>
            this.addSessionBundle()
          )
        } else {
          this.setState({ bundle: prevLocaleBundle, isLoading: false, lastModified })
        }
      })
  }

  render() {
    const { children, defaultLocale } = this.props
    let { bundle, currLocale } = this.state
    if (!currLocale) {
      currLocale = defaultLocale
    }
    if (!bundle) {
      bundle = {}
    }
    return (
      <IntlProvider defaultLocale={defaultLocale} locale={currLocale} messages={bundle}>
          {children}
      </IntlProvider>
    )
  }
}

export default BundleProvider
